import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AccountingFileFolder, CreateFileAttachmentInput } from '@releve/shared';
import { supabase } from '@/integrations/supabase/client';
import { authedDelete, authedPost } from '@/lib/api';
import { vaultAttachmentsPath, vaultFilePath } from '@/hooks/useVaultMutations';
import { toast } from '@/hooks/useToast';
import { captureException } from '@/lib/sentry';
import { storageSafeFileName } from '@releve/shared';
import { salesChannelsKey } from '@/hooks/useSalesChannels';

/**
 * WRITES GO THROUGH THE BACKEND. `supabase` survives here for the storage
 * upload (browser-side by design; docs/BACKEND_API.md) and the junction
 * SELECTs only — the `files` + `transaction_attachments` INSERT pair is now
 * ONE atomic `POST /api/v1/files/orgs/:orgId/attachments`, and the `files`
 * DELETE is `DELETE /api/v1/files/orgs/:orgId/files/:fileId?attachment_id=:id`.
 */

export type AttachmentType = 'receipt' | 'invoice' | 'supporting_doc';

export interface TransactionAttachment {
  id: string;
  ledger_transaction_id?: string;
  ap_invoice_id?: string;
  ar_invoice_id?: string;
  file_id: string;
  attachment_type: AttachmentType;
  created_at: string;
  created_by: string;
  file: {
    id: string;
    name: string;
    file_path: string;
    file_size: number;
    content_type: string;
  };
}

/** The parent columns an attachment list can be read by. */
const PARENT_COLUMNS = ['ledger_transaction_id', 'ap_invoice_id', 'ar_invoice_id'] as const;

/**
 * The attachments under any of the given parents (OR-ed) — the read behind
 * {@link useTransactionAttachments}, exported for a caller that needs it
 * outside a query (the inbox match skips files the record already holds).
 */
export async function fetchTransactionAttachments(
  transactionId?: string,
  apInvoiceId?: string,
  arInvoiceId?: string,
): Promise<TransactionAttachment[]> {
  const keys = PARENT_COLUMNS
    .map((column, i) => [column, [transactionId, apInvoiceId, arInvoiceId][i]] as const)
    .filter((key): key is readonly [(typeof PARENT_COLUMNS)[number], string] => !!key[1]);
  if (keys.length === 0) return [];

  const base = supabase
    .from('transaction_attachments')
    .select(`
      *,
      file:files(id, name, file_path, file_size, content_type)
    `);

  // Safe to interpolate into .or(): every id is a uuid this app minted.
  const query =
    keys.length === 1
      ? base.eq(keys[0][0], keys[0][1])
      : base.or(keys.map(([column, id]) => `${column}.eq.${id}`).join(','));

  const { data, error } = await query.order('created_at', { ascending: true });

  if (error) throw error;
  return (data || []) as TransactionAttachment[];
}

/**
 * Hook to fetch attachments for a transaction, an AP bill and/or an AR invoice.
 * Every id supplied is OR-ed: an approved bill's attachments may sit on the
 * bill (attached before approval) or its journal, and an AR invoice's on the
 * invoice (every attachment since post-on-issue, 20261101120000 — a draft has
 * no journal) or, for an invoice from before, its journal.
 */
export const useTransactionAttachments = (
  transactionId?: string,
  apInvoiceId?: string,
  arInvoiceId?: string,
) => {
  return useQuery({
    queryKey: ['transaction-attachments', transactionId, apInvoiceId, arInvoiceId],
    queryFn: () => fetchTransactionAttachments(transactionId, apInvoiceId, arInvoiceId),
    enabled: !!transactionId || !!apInvoiceId || !!arInvoiceId,
  });
};

/**
 * Refresh every attachment list an upload / delete under these parents could
 * appear in. The AR invoice list is matched by its key position (index 3) —
 * its key may also carry the invoice's journal id in front.
 */
function invalidateAttachmentLists(
  queryClient: ReturnType<typeof useQueryClient>,
  { transactionId, apInvoiceId, arInvoiceId }: { transactionId?: string; apInvoiceId?: string; arInvoiceId?: string },
) {
  if (transactionId) {
    queryClient.invalidateQueries({ queryKey: ['transaction-attachments', transactionId] });
  }
  if (apInvoiceId) {
    queryClient.invalidateQueries({ queryKey: ['transaction-attachments', undefined, apInvoiceId] });
  }
  if (arInvoiceId) {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'transaction-attachments' && query.queryKey[3] === arInvoiceId,
    });
  }
}

/**
 * Hook to upload attachments for a transaction, an AP bill or an AR invoice.
 * Exactly one parent is written: an AR invoice when given (every new AR
 * attachment keys on the invoice, draft or issued — 20261101120000), else
 * the journal, else the AP bill.
 */
export const useUploadTransactionAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      transactionId,
      apInvoiceId,
      arInvoiceId,
      files,
      attachmentType = 'supporting_doc',
      entityType, // the storage folder — one of ACCOUNTING_FILE_FOLDERS
      entityId,
    }: {
      organizationId: string;
      transactionId?: string;
      apInvoiceId?: string;
      /** An AR invoice — wins over `transactionId` (see the hook's note). */
      arInvoiceId?: string;
      files: File[];
      attachmentType?: AttachmentType;
      /**
       * The storage folder (`<org>/<folder>/<entityId>/…`). Typed to the shared
       * ACCOUNTING_FILE_FOLDERS: a journal / AP-bill attachment outside them is
       * refused 422 by the API (org roles step 3 — the folder is what hides a
       * receipt from roles without the `accounting` scope).
       */
      entityType: AccountingFileFolder;
      entityId: string;
    }) => {
      if (!transactionId && !apInvoiceId && !arInvoiceId) {
        throw new Error('One of transactionId, apInvoiceId or arInvoiceId must be provided');
      }

      // No `supabase.auth.getUser()` here: `created_by` is pinned server-side
      // from the verified token, so the browser never supplies an identity.
      const uploadedFiles: TransactionAttachment[] = [];

      for (const file of files) {
        try {
          // 1. Upload file to storage
          // Key-safe object name (Storage rejects e.g. the narrow no-break
          // space in macOS screenshot names); `name` below keeps the original.
          const safeName = storageSafeFileName(file.name);
          const storagePath = `${organizationId}/${entityType}/${entityId}/${safeName}`;
          const { error: uploadError } = await supabase.storage
            .from('files')
            .upload(storagePath, file, {
              cacheControl: '3600',
              upsert: false,
            });

          let finalPath = storagePath;
          let finalFileName = file.name;

          if (uploadError) {
            // If file already exists, try with timestamp
            const timestamp = Date.now();
            const fileExt = file.name.split('.').pop();
            const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
            finalFileName = `${fileNameWithoutExt}-${timestamp}.${fileExt}`;
            const safeDot = safeName.lastIndexOf('.');
            const safeStem = safeDot > 0 ? safeName.slice(0, safeDot) : safeName;
            const safeExt = safeDot > 0 ? safeName.slice(safeDot) : '';
            finalPath = `${organizationId}/${entityType}/${entityId}/${safeStem}-${timestamp}${safeExt}`;

            const { error: retryError } = await supabase.storage
              .from('files')
              .upload(finalPath, file, {
                cacheControl: '3600',
                upsert: false,
              });

            if (retryError) throw retryError;
          }

          // 2. Create the file row AND the junction row in ONE atomic call.
          //    `file_type` is NOT sent — the handler resolves it (the
          //    attachment_type, for a non-activity parent), and the body
          //    schema is `.strict()`, so the extra key would 400.
          //
          //    EXACTLY ONE PARENT, AND ledger_transaction_id WINS. The old
          //    code set BOTH columns whenever both ids were passed; the
          //    endpoint's discriminated union cannot express that and
          //    `create_file_attachment` raises 22023 on it. The only caller
          //    that passes both is CreateAPInvoiceForm's edit-of-an-approved-
          //    bill path, where the ledger transaction is the posted document
          //    and the AP invoice is its draft ancestor — so the ledger
          //    transaction is the parent to keep. Nothing is lost on the read
          //    side: `useTransactionAttachments` ORs the two columns whenever
          //    it is given both ids.
          //    An AR invoice outranks its journal: every new AR attachment
          //    keys on the invoice, so it is the same parent before and after
          //    Issue (a draft has no journal to hang it on).
          const parent: CreateFileAttachmentInput['parent'] = arInvoiceId
            ? { kind: 'ar_invoice', ar_invoice_id: arInvoiceId }
            : transactionId
              ? { kind: 'ledger_transaction', ledger_transaction_id: transactionId }
              : { kind: 'ap_invoice', ap_invoice_id: apInvoiceId! };

          const { attachment } = await authedPost<{ attachment: TransactionAttachment }>(
            vaultAttachmentsPath(organizationId),
            {
              parent,
              attachment_type: attachmentType,
              file: {
                name: finalFileName,
                file_path: finalPath,
                file_size: file.size,
                content_type: file.type,
              },
            } satisfies CreateFileAttachmentInput,
          );

          uploadedFiles.push(attachment);
        } catch (error) {
          captureException(error instanceof Error ? error : new Error(`Failed to upload file ${file.name}`));
          // Continue with other files even if one fails
        }
      }

      return uploadedFiles;
    },
    onSuccess: (_, { organizationId, transactionId, apInvoiceId, arInvoiceId }) => {
      invalidateAttachmentLists(queryClient, { transactionId, apInvoiceId, arInvoiceId });
      // The rows these uploads create carry organization_id, so they show up in
      // the Vault — which never used to be refreshed after an upload.
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
      // A POS sale reads its files across every journal it has had (not by
      // transactionId), so its list lives under its own key.
      queryClient.invalidateQueries({
        queryKey: [...salesChannelsKey(organizationId), 'saleAttachments'],
      });
    },
    onError: (error: Error) => {
      toast.error('Upload failed', {
        description: error.message || 'Failed to upload attachments',
      });
    },
  });
};

/**
 * Hook to delete a transaction attachment
 * Supports deleting attachments linked to either transactions or AP invoices
 */
export const useDeleteTransactionAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      attachmentId,
    }: {
      organizationId: string;
      attachmentId: string;
      /** Not used by the write — all three are invalidation keys in onSuccess. */
      transactionId?: string;
      apInvoiceId?: string;
      arInvoiceId?: string;
    }) => {
      // 1. Resolve attachment -> file. A READ, so it stays on supabase.
      const { data: attachment, error: readError } = await supabase
        .from('transaction_attachments')
        .select('file_id')
        .eq('id', attachmentId)
        .single();

      if (readError) throw readError;
      // The old code SILENTLY no-oped here, so a vanished junction row looked
      // like a successful delete. Now it surfaces through the error toast.
      if (!attachment?.file_id) {
        throw new Error('Attachment not found');
      }

      // 2. ONE call: the backend pre-reads the row, best-effort removes the
      //    storage object and deletes the FILES row. The junction row goes
      //    with it — transaction_attachments.file_id is ON DELETE CASCADE, so
      //    the explicit junction delete is the redundant statement, never the
      //    files delete (dropping THAT would orphan the file row in the Vault).
      //
      //    `?attachment_id` says WHICH attachment this delete comes from. The
      //    endpoint counts the file's OTHER references and 409s if any remain:
      //    a file only this document holds is deleted, one something else also
      //    holds is refused. The Vault's own Delete omits the parameter and is
      //    therefore refused for any attached file at all.
      await authedDelete<unknown>(
        vaultFilePath(organizationId, attachment.file_id, attachmentId),
      );
    },
    onSuccess: (_, { organizationId, transactionId, apInvoiceId, arInvoiceId }) => {
      invalidateAttachmentLists(queryClient, { transactionId, apInvoiceId, arInvoiceId });
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
      queryClient.invalidateQueries({
        queryKey: [...salesChannelsKey(organizationId), 'saleAttachments'],
      });
      toast.success('Attachment deleted', {
        description: 'Attachment has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast.error('Delete failed', {
        description: error.message || 'Failed to delete attachment',
      });
    },
  });
};
